import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Box,
  makeStyles,
  Card,
  CardContent,
} from "@material-ui/core";

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CancelIcon from "@material-ui/icons/Cancel";
import ImageIcon from "@material-ui/icons/Image";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import VisibilityIcon from "@material-ui/icons/Visibility";

import { uploadBanner } from "../../../adminModel/setting";
import { useAlert } from "react-alert";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 14,
      width: "100%",
      maxWidth: 850,
      margin: theme.spacing(2),
      overflow: "hidden",
    },
  },

  dialogTitle: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: "#fafafa",
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
  },

  titleIcon: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 10,
    backgroundColor: "#eef2ff",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  title: {
    fontSize: 17,
    fontWeight: 600,
    color: "#1f2937",
  },

  subtitle: {
    marginTop: 3,
    fontSize: 12,
    color: "#6b7280",
  },

  content: {
    padding: theme.spacing(3),
    backgroundColor: "#fff",
  },

  previewCard: {
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  sectionHeader: {
    padding: theme.spacing(1.75, 2),
    backgroundColor: "#fafafa",
    display: "flex",
    alignItems: "center",
  },

  sectionIcon: {
    color: "#6b7280",
    marginRight: 8,
    fontSize: 20,
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
  },

  previewWrapper: {
    height: 280,
    padding: theme.spacing(2),
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },

  noImage: {
    width: "100%",
    height: "100%",
    border: "1px dashed #d1d5db",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    backgroundColor: "#fff",
  },

  noImageIcon: {
    fontSize: 42,
    marginBottom: 8,
    color: "#cbd5e1",
  },

  noImageText: {
    fontSize: 12,
    color: "#9ca3af",
  },

  formCard: {
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
  },

  formContent: {
    padding: theme.spacing(2.5),
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  fieldSpacing: {
    marginBottom: theme.spacing(2),
  },

  dialogActions: {
    padding: theme.spacing(1.75, 3),
    backgroundColor: "#fafafa",
    borderTop: "1px solid #e5e7eb",
  },

  cancelButton: {
    minHeight: 40,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },

  uploadButton: {
    minHeight: 40,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
  },

  helperText: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 6,
  },
}));

export default function FormDialog(props) {
  const classes = useStyles();
  const alert = useAlert();

  const {
    open,
    onClose,
    GetData,
    Loading,
  } = props;

  const [banner, setBanner] = useState(null);

  const [values, setValues] = useState({
    banner_url: "",
    banner_link: "",
    isVisible: "",
    isMobile: "",
  });

  /* =========================================================
     RESET / LOAD CURRENT DATA
  ========================================================= */

  useEffect(() => {
    if (!GetData) {
      return;
    }

    setValues({
      banner_url:
        GetData.banner_url ?? "",
      banner_link:
        GetData.banner_link ?? "",
      isVisible:
        GetData.isVisible ?? 1,
      isMobile:
        GetData.isMobile ?? 0,
    });

    setBanner(null);
  }, [GetData, open]);

  /* =========================================================
     HANDLE CHANGE
  ========================================================= */

  const handleChange = (e) => {
    const {
      name,
      value,
      files,
    } = e.target;

    if (name === "auction_banner") {
      const file =
        files && files.length > 0
          ? files[0]
          : null;

      setBanner(file);
      return;
    }

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!GetData || !GetData.id) {
      alert.error(
        "Banner information is missing."
      );
      return;
    }

    if (!banner && !GetData.banner_url) {
      alert.error(
        "Please select a banner image."
      );
      return;
    }

    try {
      Loading(true);

      const formData = new FormData();

      /*
        Keep existing API fields/process
      */

      if (banner) {
        formData.append(
          "files",
          banner
        );
      }

      formData.append(
        "id",
        GetData.id
      );

      formData.append(
        "banner_url",
        values.banner_url ??
          GetData.banner_url ??
          ""
      );

      formData.append(
        "banner_link",
        values.banner_link ??
          GetData.banner_link ??
          ""
      );

      formData.append(
        "isSequence",
        GetData.isSequence
      );

      formData.append(
        "isVisible",
        values.isVisible ??
          GetData.isVisible
      );

      formData.append(
        "isMobile",
        values.isMobile ??
          GetData.isMobile
      );

      console.log(
        "banner_url:",
        values.banner_url ??
          GetData.banner_url
      );

      const results =
        await uploadBanner(
          formData
        );

      alert.success(results);

      setBanner(null);

      onClose(false);
    } catch (error) {
      console.error(
        "Upload banner error:",
        error
      );

      alert.error(
        "Failed to upload banner."
      );
    } finally {
      Loading(false);
    }
  };

  /* =========================================================
     CLOSE
  ========================================================= */

  const handleClose = () => {
    setBanner(null);
    onClose(false);
  };

  /* =========================================================
     PREVIEW IMAGE
  ========================================================= */

  const previewImage = banner
    ? URL.createObjectURL(banner)
    : GetData &&
      GetData.banner_url
    ? GetData.banner_url
    : "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="upload-banner-title"
      className={classes.dialog}
      maxWidth="md"
      fullWidth
    >
      <form
        onSubmit={handleFormSubmit}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <DialogTitle
          id="upload-banner-title"
          className={classes.dialogTitle}
        >
          <Box
            className={classes.titleRow}
          >

            <Box
              className={classes.titleIcon}
            >
              <CloudUploadIcon />
            </Box>

            <Box>

              <Typography
                className={
                  classes.title
                }
              >
                Upload Banner
              </Typography>

              <Typography
                className={
                  classes.subtitle
                }
              >
                Update the banner image and
                display settings.
              </Typography>

            </Box>

          </Box>
        </DialogTitle>

        <Divider />

        {/* =================================================
            CONTENT
        ================================================= */}

        <DialogContent
          className={classes.content}
        >

          <Grid
            container
            spacing={3}
          >

            {/* =============================================
                IMAGE PREVIEW
            ============================================= */}

            <Grid
              item
              xs={12}
              md={5}
            >

              <Card
                className={
                  classes.previewCard
                }
              >

                <Box
                  className={
                    classes.sectionHeader
                  }
                >

                  <ImageIcon
                    className={
                      classes.sectionIcon
                    }
                  />

                  <Typography
                    className={
                      classes.sectionTitle
                    }
                  >
                    Banner Preview
                  </Typography>

                </Box>

                <Divider />

                <CardContent
                  className={
                    classes.previewWrapper
                  }
                >

                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Banner preview"
                      className={
                        classes.previewImage
                      }
                    />
                  ) : (
                    <Box
                      className={
                        classes.noImage
                      }
                    >
                      <ImageIcon
                        className={
                          classes.noImageIcon
                        }
                      />

                      <Typography
                        className={
                          classes.noImageText
                        }
                      >
                        No banner image
                      </Typography>
                    </Box>
                  )}

                </CardContent>

              </Card>

            </Grid>

            {/* =============================================
                FORM
            ============================================= */}

            <Grid
              item
              xs={12}
              md={7}
            >

              <Card
                className={
                  classes.formCard
                }
              >

                <Box
                  className={
                    classes.sectionHeader
                  }
                >

                  <Typography
                    className={
                      classes.sectionTitle
                    }
                  >
                    Banner Settings
                  </Typography>

                </Box>

                <Divider />

                <CardContent
                  className={
                    classes.formContent
                  }
                >

                  {/* IMAGE */}

                  <TextField
                    fullWidth
                    type="file"
                    name="auction_banner"
                    label="Banner Image"
                    onChange={
                      handleChange
                    }
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    className={
                      classes.fieldSpacing
                    }
                    inputProps={{
                      accept:
                        "image/*",
                    }}
                  />

                  <Typography
                    className={
                      classes.helperText
                    }
                  >
                    Select a new image to
                    replace the current banner.
                  </Typography>

                  {/* BANNER URL */}

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Banner URL"
                    name="banner_url"
                    value={
                      values.banner_url
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    variant="outlined"
                    onChange={
                      handleChange
                    }
                    className={
                      classes.fieldSpacing
                    }
                  />

                  {/* BANNER LINK */}

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Banner Link"
                    name="banner_link"
                    value={
                      values.banner_link
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    variant="outlined"
                    onChange={
                      handleChange
                    }
                    className={
                      classes.fieldSpacing
                    }
                  />

                  {/* MOBILE / DESKTOP */}

                  <TextField
                    fullWidth
                    select
                    margin="normal"
                    label="Display Layout"
                    name="isMobile"
                    value={
                      values.isMobile
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    onChange={
                      handleChange
                    }
                    className={
                      classes.fieldSpacing
                    }
                  >

                    <MenuItem
                      value={1}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <PhoneIphoneIcon
                          fontSize="small"
                          style={{
                            marginRight: 8,
                          }}
                        />
                        Mobile
                      </Box>
                    </MenuItem>

                    <MenuItem
                      value={0}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <DesktopWindowsIcon
                          fontSize="small"
                          style={{
                            marginRight: 8,
                          }}
                        />
                        Desktop
                      </Box>
                    </MenuItem>

                  </TextField>

                  {/* SHOW / HIDE */}

                  <TextField
                    fullWidth
                    select
                    margin="normal"
                    label="Visibility"
                    name="isVisible"
                    value={
                      values.isVisible
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    onChange={
                      handleChange
                    }
                  >

                    <MenuItem
                      value={1}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <VisibilityIcon
                          fontSize="small"
                          style={{
                            marginRight: 8,
                          }}
                        />
                        Show
                      </Box>
                    </MenuItem>

                    <MenuItem
                      value={0}
                    >
                      Hide
                    </MenuItem>

                  </TextField>

                </CardContent>

              </Card>

            </Grid>

          </Grid>

        </DialogContent>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <DialogActions
          className={
            classes.dialogActions
          }
        >

          <Button
            onClick={handleClose}
            variant="outlined"
            className={
              classes.cancelButton
            }
            startIcon={
              <CancelIcon />
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            color="primary"
            variant="contained"
            className={
              classes.uploadButton
            }
            startIcon={
              <CloudUploadIcon />
            }
          >
            Upload Banner
          </Button>

        </DialogActions>

      </form>
    </Dialog>
  );
}

FormDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  GetData: PropTypes.object,
  Loading: PropTypes.func,
};

FormDialog.defaultProps = {
  open: false,
  onClose: () => {},
  GetData: {},
  Loading: () => {},
};